import { forwardRef, useState } from 'react'

import { Card } from '../../components/Card'
import { CouponInput } from '../../components/CouponInput'
import { CurrencySelector } from '../../components/CurrencySelector'
import { Skeleton } from '../../components/Skeleton'
import { ChevronIcon, OrderIcon } from '../../icons'
import { CartItem } from '../CartItem'
import styles from './OrderSummary.module.css'

import type { CurrencyOption } from '../../components/CurrencySelector'
import type { OrderSummaryLabels } from '../../types'
import type { Cart } from '@checkout/core'

export interface OrderSummaryProps {
  /** Shopping cart data */
  cart: Cart
  /** Whether data is loading */
  isLoading?: boolean
  /** Error message to display */
  error?: string | null
  /** Whether to show product images */
  showImages?: boolean
  /** Currency formatting function */
  formatCurrency: (amount: number, currency: string) => string
  /** Title text */
  title?: string
  /** Whether to show the title icon */
  showTitleIcon?: boolean
  /** Whether to show currency selector */
  showCurrencySelector?: boolean
  /** Available currencies for selector */
  currencies?: CurrencyOption[]
  /** Current currency code */
  selectedCurrency?: string
  /** Callback when currency changes */
  onCurrencyChange?: (currencyCode: string) => void
  /** Whether to show coupon input */
  showCouponInput?: boolean
  /** Coupon link text */
  couponLinkText?: string
  /** Coupon input placeholder */
  couponPlaceholder?: string
  /** Apply button text */
  couponApplyText?: string
  /** Coupon link click handler */
  onCouponClick?: () => void
  /** Coupon apply handler */
  onCouponApply?: (code: string) => void
  /** Whether coupon is being applied */
  isCouponLoading?: boolean
  /** Whether coupon was successfully applied */
  isCouponSuccess?: boolean
  /** Coupon error message */
  couponError?: string
  /** Whether to show tax row */
  showTax?: boolean
  /** Labels for i18n */
  labels?: OrderSummaryLabels
  /** Additional class name */
  className?: string
}

const defaultLabels: Required<OrderSummaryLabels> = {
  showOrderSummary: 'Show order summary',
  hideOrderSummary: 'Hide order summary',
  subtotal: 'Subtotal',
  discount: 'Discount',
  additionalDiscount: 'Additional Discount',
  tax: 'Tax',
  total: 'Total',
}

export const OrderSummary = forwardRef<HTMLDivElement, OrderSummaryProps>(
  (
    {
      cart,
      isLoading = false,
      error,
      showImages = false,
      formatCurrency,
      title = 'Order Summary',
      showTitleIcon = true,
      showCurrencySelector = false,
      currencies = [],
      selectedCurrency,
      onCurrencyChange,
      showCouponInput = false,
      couponLinkText = 'I have a coupon',
      couponPlaceholder = 'Enter coupon code',
      couponApplyText = 'Apply',
      onCouponClick,
      onCouponApply,
      isCouponLoading = false,
      isCouponSuccess = false,
      couponError,
      showTax = false,
      labels: customLabels,
      className,
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(true)
    const [isCouponExpanded, setIsCouponExpanded] = useState(false)

    const labels = { ...defaultLabels, ...customLabels }

    const toggleExpanded = () => setIsExpanded(!isExpanded)
    const toggleCouponExpanded = () => setIsCouponExpanded(!isCouponExpanded)

    const handleCurrencyChange = (code: string) => {
      onCurrencyChange?.(code)
    }

    const handleCouponApply = (code: string) => {
      onCouponApply?.(code)
    }

    return (
      <Card ref={ref} className={`${styles.container} ${className ?? ''}`} padding="none">
        {/* Mobile toggle header */}
        <button
          type="button"
          className={styles.mobileHeader}
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          data-ga-action-name={isExpanded ? 'order_summary_collapse' : 'order_summary_expand'}
          data-ga-module="order_summary"
        >
          <span className={styles.mobileHeaderText}>
            <ChevronIcon
              direction={isExpanded ? 'up' : 'down'}
              className={styles.chevron}
              width={16}
              height={16}
            />
            {isExpanded ? labels.hideOrderSummary : labels.showOrderSummary}
          </span>
          <span className={styles.mobileTotal}>
            {isLoading || error ? '—' : formatCurrency(cart.total, cart.currency)}
          </span>
        </button>

        {/* Content */}
        <div className={`${styles.content} ${isExpanded ? styles.expanded : ''}`}>
          {/* Header: Title with icon + Currency selector */}
          <div className={styles.header}>
            <div className={styles.titleRow}>
              {showTitleIcon && <OrderIcon size={22} className={styles.titleIcon} />}
              <h2 className={styles.title}>{title}</h2>
            </div>
            {showCurrencySelector && currencies.length > 0 && (
              <CurrencySelector
                currencies={currencies}
                value={selectedCurrency || cart.currency}
                onChange={handleCurrencyChange}
              />
            )}
          </div>

          {/* Divider after title */}
          <div className={styles.divider} />

          {/* Loading state */}
          {isLoading && (
            <div className={styles.loadingState}>
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className={styles.errorState}>
              <p>{error}</p>
            </div>
          )}

          {/* Cart items */}
          {!isLoading && !error && (
            <>
              <div className={styles.items}>
                {cart.items.map((item, index) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    showImage={showImages}
                    formatCurrency={formatCurrency}
                    isLast={index === cart.items.length - 1}
                  />
                ))}
              </div>

              {/* Divider before coupon */}
              <div className={styles.dividerCoupon} />

              {/* Coupon section */}
              {showCouponInput && onCouponApply ? (
                <div className={styles.couponSection}>
                  <button
                    type="button"
                    className={`${styles.couponLink} ${isCouponExpanded ? styles.couponLinkActive : ''}`}
                    onClick={toggleCouponExpanded}
                    data-ga-action-name="coupon_section_expand"
                    data-ga-module="coupon"
                  >
                    {couponLinkText}
                  </button>
                  <div
                    className={`${styles.couponInputWrapper} ${isCouponExpanded ? styles.couponInputExpanded : ''}`}
                  >
                    <CouponInput
                      placeholder={couponPlaceholder}
                      applyText={couponApplyText}
                      onApply={handleCouponApply}
                      isLoading={isCouponLoading}
                      isSuccess={isCouponSuccess}
                      error={couponError}
                    />
                  </div>
                </div>
              ) : onCouponClick ? (
                <button type="button" className={styles.couponLink} onClick={onCouponClick}>
                  {couponLinkText}
                </button>
              ) : null}

              {/* Totals */}
              <div className={styles.totals}>
                <div className={styles.subtotalRow}>
                  <span className={styles.subtotalLabel}>{labels.subtotal}</span>
                  <span className={styles.subtotalValue}>
                    {formatCurrency(cart.subtotal, cart.currency)}
                  </span>
                </div>

                {cart.discount > 0 && (
                  <div className={styles.discountRow}>
                    <span>{cart.discountCode ? labels.additionalDiscount : labels.discount}</span>
                    <span className={styles.discountValue}>
                      -{formatCurrency(cart.discount, cart.currency)}
                    </span>
                  </div>
                )}

                {showTax && cart.tax > 0 && (
                  <div className={styles.taxRow}>
                    <span>{labels.tax}</span>
                    <span>{formatCurrency(cart.tax, cart.currency)}</span>
                  </div>
                )}

                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{labels.total}</span>
                  <span className={styles.totalValue}>
                    {formatCurrency(cart.total, cart.currency)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    )
  }
)

OrderSummary.displayName = 'OrderSummary'
